import React, { useRef, useEffect, useState, useMemo } from 'react';
import { generateLetterOffsets } from '../00-shared/constants';
import { AnimatedText } from '../00-shared/AnimatedText';

interface ValuePropositionSectionProps {
    highlightColor: {
        primary: string;
        hover: string;
        gradient: {
            from: string;
            to: string;
            fromBlue: string;
            toPurple: string;
        };
    };
}

const ValuePropositionSection: React.FC<ValuePropositionSectionProps> = ({ highlightColor }) => {
    const [titleAnimated, setTitleAnimated] = useState(false);
    const titleRef = useRef<HTMLHeadingElement>(null);
    const titleText = 'Smarter tools for Coachs. Better Outcomes for Athletes.';
    const subtitleText = 'CGM Sports supports teams and athletes simplify their data and enhance performance.';
    
    const titleLetterOffsets = useMemo(() => generateLetterOffsets(titleText, 100, 200), []);
    const subtitleLetterOffsets = useMemo(() => generateLetterOffsets(subtitleText, 100, 100), []);

    const processSteps = [
        {
            number: '01',
            title: 'Data Ingestion',
            description: 'Seamlessly connect wearable, testing, and wellness data across your team.',
            action: 'Select your own hardware',
            image: '/images/home-valueprops-team-performance.jpg'
        },
        {
            number: '02',
            title: 'Workflows',
            description: 'Automate reports and readiness alerts that fit your team’s training schedule.',
            action: 'Explore AI Features',
            image: '/images/home-valueprops-data-integration.jpg'
        },
        {
            number: '03',
            title: 'Analysis',
            description: 'Turn complex performance data into clear, actionable insights across strength, recovery, and readiness.',
            action: 'View Dashboard',
            image: '/images/home-valueprops-analytics.jpg'
        },
        {
            number: '04',
            title: 'Security',
            description: 'HIPAA & FERPA compliant. Your data stays secure and under your control.',
            action: 'View Dashboard',
            image: '/images/home-valueprops-insights.jpg'
        },
    ];

    useEffect(() => {
        if (!titleRef.current) return;

        const observerOptions = {
            threshold: 0.1,
            rootMargin: '0px 0px 200px 0px'
        };

        const observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting && !titleAnimated) {
                    setTitleAnimated(true);
                }
            });
        }, observerOptions);

        observer.observe(titleRef.current);

        return () => observer.disconnect();
    }, [titleAnimated]);

    useEffect(() => {
        const observerOptions = {
            threshold: 0.1,
            rootMargin: '0px 0px -100px 0px'
        };

        const observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    const index = parseInt(entry.target.getAttribute('data-index') || '0');
                    const animationClass = index % 2 === 0 ? 'fade-in-left' : 'fade-in-right';
                    entry.target.classList.add(animationClass);
                }
            });
        }, observerOptions);

        document.querySelectorAll('.observe-fade-directional').forEach(el => observer.observe(el));

        return () => observer.disconnect();
    }, []);

    return (
        <section className="py-20 px-4 sm:px-6 lg:px-8 bg-black relative">
            <div className="max-w-7xl mx-auto relative z-10">
                <div className="pb-24 text-center">
                    <AnimatedText
                        as="h2"
                        text={titleText}
                        offsets={titleLetterOffsets}
                        animated={titleAnimated}
                        className="text-3xl md:text-5xl text-white mb-4"
                    />
                    <div ref={titleRef}>
                        <AnimatedText
                            text={subtitleText}
                            offsets={subtitleLetterOffsets}
                            animated={titleAnimated}
                        />
                    </div>
                </div>
                <div className="space-y-16">
                    {processSteps.map((step, index) => (
                        <div
                            key={index}
                            data-index={index}
                            className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center observe-fade-directional opacity-0 ${index % 2 === 1 ? 'lg:flex-row-reverse' : ''}`}
                        >
                            <div className={`${index % 2 === 1 ? 'lg:order-2' : 'lg:text-right'}`}>
                                <div className="text-6xl font-bold mb-4">
                                    <span 
                                        className="bg-clip-text text-transparent font-bold"
                                        style={{
                                            backgroundImage: `linear-gradient(to right, ${highlightColor.gradient.fromBlue}, ${highlightColor.gradient.toPurple})`,
                                            WebkitBackgroundClip: 'text',
                                            WebkitTextFillColor: 'transparent'
                                        }}
                                    >
                                        {step.number}
                                    </span>
                                </div>
                                <h3 className="text-3xl md:text-4xl font-bold text-white mb-6">
                                    {step.title}
                                </h3>
                                <p className="text-lg text-gray-300 leading-relaxed mb-6">
                                    {step.description}
                                </p>
                                {/* <button 
                                    className="px-6 py-3 text-white rounded-lg transition-colors font-medium"
                                    style={{ 
                                        backgroundColor: highlightColor.primary,
                                        '--hover-bg': highlightColor.hover
                                    } as React.CSSProperties}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.backgroundColor = highlightColor.hover;
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.backgroundColor = highlightColor.primary;
                                    }}
                                >
                                </button>
                                    {step.action} */}
                            </div>
                            <div className={`${index % 2 === 1 ? 'lg:order-1' : ''} flex items-center justify-center`}>
                                <img
                                    src={step.image}
                                    alt={step.title}
                                    className="w-full h-60 object-cover rounded-xl border border-neutral-800 shadow-amber-600"
                                />
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
};

export default ValuePropositionSection;

